import { FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Admin → Reports tab.
 * Lists downloadable CSV exports (sales, customers, P&L, products).
 * Extracted from AdminDashboard.jsx to keep that file maintainable.
 */
export function ReportsPanel() {
  return (
    <div data-testid="reports-tab" className="space-y-6">
      <div className="bg-[#FDFBF7] rounded-3xl thick-gold p-6">
        <h3 className="font-serif text-2xl">Reports &amp; exports</h3>
        <p className="text-sm text-[#5C5547] mt-1 max-w-xl">
          Download your business data as CSV files. Open them in Excel / Google Sheets, or drag
          them into your <strong>Claude</strong> chat and ask Claude to turn them into a
          presentation, profit/loss breakdown, charts, anything you need.
        </p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReportCard
            title="All sales"
            desc="Every order with customer, product, variant, ship-to address, and payment details. Best for: building a presentation or analyzing trends."
            href="/api/reports/sales.csv"
            filename="sales.csv"
            testid="report-sales"
          />
          <ReportCard
            title="Customer list"
            desc="Aggregated by email: total spent, order count, first/last purchase. Best for: VIP outreach + Black Friday lists."
            href="/api/reports/customers.csv"
            filename="customers.csv"
            testid="report-customers"
          />
          <ReportCard
            title="Profit &amp; Loss (monthly)"
            desc="Revenue per month from paid orders. Add a cost column in Claude/Excel for true P&L. Best for: accountant + tax prep."
            href="/api/reports/pnl.csv"
            filename="profit_and_loss.csv"
            testid="report-pnl"
          />
          <ReportCard
            title="Top-selling products"
            desc="Units sold + revenue per product, ranked best to worst. Best for: deciding what to restock or discontinue."
            href="/api/reports/products.csv"
            filename="product_performance.csv"
            testid="report-products"
          />
        </div>
        <div className="mt-6 text-xs text-[#5C5547] bg-[#F5F2EA] rounded-2xl p-4 leading-relaxed">
          <strong>How to use with Claude:</strong> Download a CSV → open{" "}
          <a className="underline text-[var(--accent)]" href="https://claude.ai" target="_blank" rel="noreferrer">
            claude.ai
          </a>{" "}
          → drag the file into the chat → ask "Make me a Q1 sales presentation from this" or
          "Build a profit and loss statement with a 35% margin assumption" or "Which 10 customers
          are most likely to buy jewelry next?". Claude will analyze, summarize, and build
          downloadable PPT/Excel for you.
        </div>
      </div>
    </div>
  );
}

function ReportCard({ title, desc, href, filename, testid }) {
  async function download() {
    try {
      const r = await api.get(href.replace("/api", ""), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error("Download failed.");
    }
  }
  return (
    <div className="rounded-2xl border border-[#EAE4D3] bg-[#FDFBF7] p-5" data-testid={testid}>
      <div className="font-serif text-lg" dangerouslySetInnerHTML={{ __html: title }} />
      <p className="text-xs text-[#5C5547] mt-1 leading-relaxed">{desc}</p>
      <button
        onClick={download}
        data-testid={`${testid}-download`}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--accent)] text-white text-xs font-semibold"
      >
        <FileText className="w-3.5 h-3.5" /> Download CSV
      </button>
    </div>
  );
}
